"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useReducedMotion } from "./useReducedMotion";

const FAMILY = "/developers/family";

/** The wordmark cut into its six letters (each its own small layer, so a rising letter is letter-sized). */
const LETTERS = [
  {
    clip: "V",
    box: "0 0 960 1132",
    left: "0.000%",
    width: "25.210%",
    points: "0 0,960 0,960 340,918 340,918 1132,0 1132",
  },
  {
    clip: "e",
    box: "918 0 622 1132",
    left: "24.107%",
    width: "16.334%",
    points: "918 340,1540 340,1540 1132,918 1132",
  },
  {
    clip: "s",
    box: "1540 0 602 1132",
    left: "40.441%",
    width: "15.809%",
    points: "1540 0,2142 0,2142 1132,1540 1132",
  },
  {
    clip: "t",
    box: "2142 0 418 1132",
    left: "56.250%",
    width: "10.977%",
    points: "2142 0,2560 0,2560 1132,2142 1132",
  },
  {
    clip: "r",
    box: "2560 0 616 1132",
    left: "67.227%",
    width: "16.176%",
    points: "2560 0,3176 0,3176 1132,2560 1132",
  },
  {
    clip: "a",
    box: "3176 0 632 1132",
    left: "83.403%",
    width: "16.597%",
    points: "3176 0,3808 0,3808 1132,3176 1132",
  },
];

/** The team, each sitting on a letter, with what they are up to after hours. */
const SITS = [
  {
    k: "bash",
    name: "Bash",
    x: 3.68,
    y: 0,
    w: 7.06,
    seat: 0.641,
    px: [238, 560],
    tip: "Coffee first, then your 9 am brief.",
  },
  {
    k: "scout",
    name: "Scout",
    x: 32.56,
    y: 30.57,
    w: 7.41,
    seat: 0.618,
    px: [250, 560],
    tip: "Three competitors shipped today. Summary at 8.",
  },
  {
    k: "quill",
    name: "Quill",
    x: 48.58,
    y: 30.39,
    w: 8.87,
    seat: 0.625,
    px: [299, 560],
    tip: "Drafting Friday’s newsletter in your voice.",
  },
  {
    k: "ledger",
    name: "Ledger",
    x: 64.21,
    y: 32.16,
    w: 7.54,
    seat: 0.631,
    px: [254, 560],
    tip: "Books balance. Now the business pages.",
  },
  {
    k: "hunter",
    name: "Hunter",
    x: 79.04,
    y: 31.1,
    w: 11.65,
    seat: 0.993,
    px: [300, 560],
    tip: "Twelve intros sent. One already replied.",
  },
  {
    k: "keeper",
    name: "Keeper",
    x: 91.65,
    y: 30.39,
    w: 6.53,
    seat: 0.603,
    px: [220, 560],
    tip: "Every customer answered. Watering the office plant.",
  },
] as const;

/** The plane's loop over the word, in the wordmark's units (the sky reaches 1000 above it). */
const FLIGHT =
  "M3165 -410 C2700 -560 1500 -560 900 -470 C520 -410 360 -300 520 -220 C800 -90 1500 -170 2200 -200 C2800 -230 3150 -280 3165 -410Z";

/**
 * After hours, the family's footer scene: the letters rise when it comes into view, the team lands on them,
 * the sun comes up and the plane takes off. A tap makes one hop and say what it is doing; one hops by itself
 * every few seconds while you watch. With reduced motion it is simply there.
 */
export function AfterHours() {
  const scene = useRef<HTMLDivElement>(null);
  const flight = useRef<SVGPathElement>(null);
  const plane = useRef<HTMLDivElement>(null);
  const [go, setGo] = useState(false);
  const [hop, setHop] = useState<string | null>(null);
  const still = useReducedMotion();

  useEffect(() => {
    const el = scene.current;
    const path = flight.current;
    const p = plane.current;
    if (!el || !path || !p || still) return;
    // The plane banks along the loop: 180 frames, each facing the way it flies.
    const length = path.getTotalLength();
    const unit = 3808 / 100;
    const frames: Keyframe[] = [];
    let last: number | null = null;
    for (let k = 0; k <= 180; k++) {
      const d = ((length * k) / 180) % length;
      const at = path.getPointAtLength(d);
      const ahead = path.getPointAtLength((d + 8) % length);
      const r = Math.atan2(ahead.y - at.y, ahead.x - at.x);
      const c = Math.cos(r);
      const bank = Math.max(-1, Math.min(1, c / 0.3));
      let a = ((c < 0 ? r + Math.PI : r) * 180) / Math.PI;
      if (last !== null) {
        while (a - last > 180) a -= 360;
        while (a - last < -180) a += 360;
      }
      last = a;
      frames.push({
        transform: `translate(${(at.x / unit).toFixed(3)}cqw,${((at.y + 1000) / unit).toFixed(3)}cqw) rotate(${a.toFixed(2)}deg) scale(${bank.toFixed(3)},1)`,
      });
    }
    const fly = p.animate(frames, { duration: 14000, iterations: Infinity });
    fly.pause();
    let seen = false;
    let onScreen = false;
    let hopper: ReturnType<typeof setInterval> | null = null;
    let takeoff: ReturnType<typeof setTimeout> | null = null;
    const io = new IntersectionObserver(
      ([en]) => {
        onScreen = en.isIntersecting;
        if (onScreen) {
          setGo(true);
          if (!seen) {
            seen = true;
            takeoff = setTimeout(() => {
              if (onScreen) fly.play();
            }, 1400);
          } else fly.play();
          hopper ??= setInterval(() => {
            setHop(SITS[Math.floor(Math.random() * SITS.length)].k);
          }, 3200);
        } else {
          fly.pause();
          if (hopper) clearInterval(hopper);
          hopper = null;
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      fly.cancel();
      if (hopper) clearInterval(hopper);
      if (takeoff) clearTimeout(takeoff);
    };
  }, [still]);

  // A hop lasts as long as its spring.
  useEffect(() => {
    if (!hop) return;
    const t = setTimeout(() => setHop(null), 520);
    return () => clearTimeout(t);
  }, [hop]);

  return (
    <div className={go || still ? "after go" : "after"} ref={scene}>
      <div className="sunrise" aria-hidden="true">
        <svg viewBox="0 0 64 64">
          <use href="#vf-sun-art" />
        </svg>
      </div>
      {["c1", "c2", "c3"].map((c) => (
        <Image
          key={c}
          className={`cloud ${c}`}
          src={`${FAMILY}/cloud-b.webp`}
          width={400}
          height={143}
          alt=""
          aria-hidden="true"
          unoptimized
        />
      ))}
      <div className="stage">
        <svg
          className="lt-defs"
          width="0"
          height="0"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient
              id="vf-lf-black"
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2="0"
              y2="1132"
            >
              <stop offset="0" stopColor="#0A0A0A" />
              <stop offset="1" stopColor="#4A4A47" />
            </linearGradient>
            {LETTERS.map((l) => (
              <clipPath key={l.clip} id={`vf-cl-${l.clip}`}>
                <polygon points={l.points} />
              </clipPath>
            ))}
          </defs>
        </svg>
        {LETTERS.map((l, i) => (
          <svg
            key={l.clip}
            className="lt"
            viewBox={l.box}
            style={{ "--i": i, left: l.left, width: l.width } as CSSProperties}
            aria-hidden="true"
            focusable="false"
          >
            <use
              href="#vf-wordmark"
              width="3808"
              height="1132"
              clipPath={`url(#vf-cl-${l.clip})`}
              fill="var(--lf)"
            />
          </svg>
        ))}
        {SITS.map((s, i) => (
          <button
            key={s.k}
            type="button"
            className={hop === s.k ? "sit hop" : "sit"}
            data-k={s.k}
            style={
              {
                "--x": `${s.x}%`,
                "--y": `${s.y}%`,
                "--w": `${s.w}%`,
                "--seat": s.seat,
                "--i": i,
              } as CSSProperties
            }
            aria-label={`${s.name}: ${s.tip}`}
            onClick={() => {
              setHop(null);
              requestAnimationFrame(() => setHop(s.k));
            }}
          >
            <Image
              src={`${FAMILY}/sit-${s.k}.webp`}
              width={s.px[0]}
              height={s.px[1]}
              alt=""
              unoptimized
            />
            {s.k === "bash" && (
              <span className="steam" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            )}
            <span className="tip">{s.tip}</span>
          </button>
        ))}
        <svg className="sky" viewBox="0 -1000 3808 2132" aria-hidden="true">
          <path ref={flight} d={FLIGHT} fill="none" />
        </svg>
        <div className="sky-h" aria-hidden="true">
          <div className="plane" ref={plane}>
            <Image
              src={`${FAMILY}/plane.webp`}
              width={500}
              height={320}
              alt=""
              unoptimized
              style={{
                left: "-4.690cqw",
                top: "-2.450cqw",
                width: "9.378cqw",
                height: "6.003cqw",
                transform: "rotate(20deg)",
                transformOrigin: "4.690cqw 2.450cqw",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
